// Deterministic baking-recipe validator. Pure JS, no framework deps.
// Imported by both the Deno edge fn (via _shared/bakingScience.ts port)
// and the client (AIRecipe.jsx) so the math is single-sourced.
//
// Philosophy: catch the failures that ratio math can detect. We do NOT
// grade the recipe or replace the chef. We surface specific structural
// issues with concrete suggestions so the user can regenerate with
// real constraints OR override and cook anyway.

import { convertWithBridge } from "./unitConvert";
import { parseAmountString } from "./nutrition";

// Reference bands. Sourced from standard baking literature (Reinhart,
// Hamelman, Corriher, Lopez-Alt). Hydration is water-equivalent weight
// as percentage of flour weight; other ratios are also % of flour.
// Tighter inner band = warning zone. Wider outer band = blocker zone.
const BANDS = {
  lean_bread: {
    hydration:  { warn: [60, 75], block: [55, 82] },
    salt:       { warn: [1.6, 2.2], block: [1.0, 3.0] },
    yeast:      { warn: [0.4, 1.5], block: [0.1, 3.0] },
  },
  enriched_bread: {
    hydration:  { warn: [48, 62], block: [42, 70] },
    fat:        { warn: [20, 50], block: [12, 65] },
    sugar:      { warn: [6, 18], block: [3, 25] },
    eggs:       { warn: [20, 40], block: [10, 55] },
    salt:       { warn: [1.4, 2.2], block: [0.8, 3.0] },
  },
  focaccia: {
    hydration:  { warn: [72, 88], block: [65, 95] },
    oil:        { warn: [4, 12], block: [2, 18] },
    salt:       { warn: [1.6, 2.2], block: [1.0, 3.0] },
  },
  cookie: {
    fat:        { warn: [40, 70], block: [25, 90] },
    sugar:      { warn: [40, 80], block: [20, 110] },
    leavening:  { warn: [0.5, 2.0], block: [0.1, 4.0] },
  },
  cake: {
    fat:        { warn: [35, 65], block: [20, 80] },
    sugar:      { warn: [70, 130], block: [50, 160] },
    eggs:       { warn: [35, 70], block: [20, 90] },
    leavening:  { warn: [2, 5], block: [1, 7] },
  },
  quick_bread: {
    liquid:     { warn: [70, 110], block: [55, 130] },
    fat:        { warn: [8, 30], block: [4, 45] },
    leavening:  { warn: [3, 6], block: [1.5, 8] },
  },
  pancake: {
    liquid:     { warn: [80, 130], block: [65, 160] },
    leavening:  { warn: [3, 8], block: [1.5, 11] },
  },
};

// Canonical-id sets for role detection. Single source so the validator
// doesn't drift from the canonical roster as new ingredients are added.
const FLOUR_IDS = new Set([
  "ap_flour", "bread_flour", "cake_flour", "pastry_flour",
  "whole_wheat_flour", "rye_flour", "spelt_flour", "00_flour",
  "semolina", "buckwheat_flour",
]);

const LEAVENING = {
  yeast:         new Set(["active_dry_yeast", "instant_yeast", "fresh_yeast"]),
  baking_powder: new Set(["baking_powder"]),
  baking_soda:   new Set(["baking_soda"]),
};

const FAT_IDS = new Set([
  "butter", "unsalted_butter", "salted_butter", "shortening",
  "lard", "olive_oil", "vegetable_oil", "canola_oil", "coconut_oil",
]);

const SUGAR_IDS = new Set([
  "granulated_sugar", "cane_sugar", "brown_sugar", "light_brown_sugar",
  "dark_brown_sugar", "powdered_sugar", "honey", "maple_syrup",
  "molasses", "corn_syrup",
]);

const ACID_IDS = new Set([
  "buttermilk", "yogurt", "sour_cream", "lemon_juice", "lime_juice",
  "vinegar", "white_vinegar", "apple_cider_vinegar", "cream_of_tartar",
  "cocoa_powder", "dark_brown_sugar", "light_brown_sugar", "honey",
  "molasses",
]);

const EGG_IDS = new Set(["egg", "eggs", "large_egg", "egg_yolk", "egg_white"]);

const LIQUID_IDS = new Set([
  "water", "milk", "whole_milk", "buttermilk", "cream", "heavy_cream",
  "yogurt", "sour_cream",
]);

const SALT_IDS = new Set(["salt", "kosher_salt", "sea_salt", "table_salt"]);

// Required flour type by baked-good category. Soft constraint — wrong
// flour is a common silent failure mode (AP flour brioche won't develop
// the gluten window). Warning only, never blocker; some recipes
// legitimately substitute.
const PREFERRED_FLOUR = {
  enriched_bread: ["bread_flour"],
  lean_bread:     ["bread_flour", "00_flour"],
  cake:           ["cake_flour", "pastry_flour", "ap_flour"],
  cookie:         ["ap_flour"],
  pastry:         ["pastry_flour", "ap_flour"],
};

// Step prose lives at .instruction in this codebase's recipe schema
// (src/data/recipes/schema.js). Fall back to .body / .text for
// safety against alternate shapes (parse-recipe-text output, future
// schema migrations).
function stepText(s) {
  return s?.instruction || s?.body || s?.text || "";
}

// ─── Public API ─────────────────────────────────────────────────────

export function isBakingRecipe(recipe) {
  const ings = recipe?.ingredients || [];
  const hasFlour = ings.some((i) => FLOUR_IDS.has(i.ingredientId));
  const hasLeavening = ings.some((i) =>
    LEAVENING.yeast.has(i.ingredientId) ||
    LEAVENING.baking_powder.has(i.ingredientId) ||
    LEAVENING.baking_soda.has(i.ingredientId)
  );
  return hasFlour && hasLeavening;
}

export function classifyBakedGood(recipe) {
  const ings = recipe?.ingredients || [];
  const tags = (recipe?.tags || []).map((t) => t.toLowerCase());
  const title = (recipe?.title || "").toLowerCase();

  // Tag-first — explicit beats inferred.
  if (tags.some((t) => /\b(brioche|challah|enriched)\b/.test(t))) return "enriched_bread";
  if (tags.some((t) => /\b(focaccia)\b/.test(t))) return "focaccia";
  if (tags.some((t) => /\b(cookie|biscotti)\b/.test(t))) return "cookie";
  if (tags.some((t) => /\b(cake|cupcake)\b/.test(t))) return "cake";
  if (tags.some((t) => /\b(pancake|crepe|waffle)\b/.test(t))) return "pancake";
  if (tags.some((t) => /\b(muffin|quick.?bread|banana.?bread)\b/.test(t))) return "quick_bread";
  if (tags.some((t) => /\b(bread|loaf|baguette|boule)\b/.test(t))) return "lean_bread";

  // Title fallback.
  if (/brioche|challah/.test(title)) return "enriched_bread";
  if (/focaccia/.test(title)) return "focaccia";
  if (/cookie/.test(title)) return "cookie";
  if (/cake|cupcake/.test(title)) return "cake";
  if (/pancake|crepe|waffle/.test(title)) return "pancake";
  if (/muffin|banana bread|zucchini bread|quick bread/.test(title)) return "quick_bread";

  // Composition-based inference.
  const grams = sumGramsByRole(ings);
  const flourG = grams.flour;
  if (flourG === 0) return "unknown";

  const yeastFlag = ings.some((i) => LEAVENING.yeast.has(i.ingredientId));
  const eggsPct = (grams.eggs / flourG) * 100;
  const fatPct = (grams.fat / flourG) * 100;
  const liquidPct = (grams.liquid / flourG) * 100;

  if (yeastFlag) {
    if (eggsPct > 15 || fatPct > 18) return "enriched_bread";
    return "lean_bread";
  }
  if (liquidPct > 75) return "pancake";
  if (fatPct > 30 && (grams.sugar / flourG) * 100 > 35) return "cookie";
  return "quick_bread";
}

export function validateBakingRecipe(recipe, ctx = {}) {
  const type = classifyBakedGood(recipe);
  const ings = recipe?.ingredients || [];
  const steps = recipe?.steps || [];
  const grams = sumGramsByRole(ings, ctx);
  const flourG = grams.flour;

  const issues = [];
  const warnings = [];

  // No flour → not actually a baking recipe. Skip cleanly.
  if (flourG === 0) {
    return { ok: true, type, hydrationPct: 0, ratios: {}, issues, warnings };
  }

  // Validator couldn't classify but the recipe IS a baking recipe —
  // surface a diagnostic warning so the user knows the math may be
  // shakier than usual.
  if (type === "unknown" && isBakingRecipe(recipe)) {
    warnings.push({
      kind: "classification_unknown",
      severity: "warning",
      message: "Validator couldn't classify this recipe (no tags or title match). Ratio checks may be incomplete.",
      suggestion: "Add a tag like 'cookie', 'bread', or 'cake' to help the validator pick the right ratio bands.",
    });
  }

  const ratios = {
    hydration:  (grams.liquid / flourG) * 100,
    fat:        (grams.fat / flourG) * 100,
    sugar:      (grams.sugar / flourG) * 100,
    eggs:       (grams.eggs / flourG) * 100,
    leavening:  (grams.chemicalLeavening / flourG) * 100,
    yeast:      (grams.yeast / flourG) * 100,
    salt:       (grams.salt / flourG) * 100,
    oil:        (grams.oil / flourG) * 100,
  };

  // CHECK 1 — leavening:flour ratio. Highest-impact catch for cookies,
  // cakes, quick breads. Off by 2x silently kills rise; recipe reads
  // fine, dough doesn't.
  if (BANDS[type]?.leavening) {
    rangeCheck(ratios.leavening, BANDS[type].leavening, {
      kind: "leavening_ratio",
      label: "Chemical leavening",
      unit: "% of flour weight",
      remedy: leaveningRemedy(type),
    }, issues, warnings);
  }

  // CHECK 2 — baking soda without acid. Silent failure: bitter, gray,
  // no rise. Soda needs an acid partner to activate.
  if (grams.bakingSoda > 0 && !hasAcid(ings)) {
    issues.push({
      kind: "soda_no_acid",
      severity: "blocker",
      message: "Recipe uses baking soda but no acidic ingredient is present.",
      suggestion: "Baking soda needs acid to activate. Either add buttermilk/yogurt/lemon/vinegar, or substitute baking powder (use ~3x the weight of the soda).",
    });
  }

  // CHECK 3 — hydration band. Most relevant for breads + focaccia +
  // pancakes; cookie/cake hydration varies too much for one band.
  if (BANDS[type]?.hydration) {
    rangeCheck(ratios.hydration, BANDS[type].hydration, {
      kind: "hydration",
      label: "Hydration",
      unit: "% (liquid weight / flour weight)",
      remedy: hydrationRemedy(type),
    }, issues, warnings);
  }

  // CHECK 4 — enriched dough butter order. Gluten development MUST
  // come before butter incorporation; butter coats gluten strands
  // and prevents them from forming. Most common cause of dense brioche.
  if (type === "enriched_bread") {
    const butterStep = findStepIndex(steps, /\b(add|incorporate|work in).*\bbutter\b/i);
    const glutenStep = findStepIndex(steps, /\b(knead|develop|window.?pane|gluten)\b/i);
    if (butterStep !== -1 && glutenStep !== -1 && butterStep < glutenStep) {
      issues.push({
        kind: "butter_too_early",
        severity: "blocker",
        message: "Butter is added before gluten development. This is the most common cause of dense brioche.",
        suggestion: "Knead the dough until it passes the windowpane test BEFORE adding butter. Then incorporate butter a tablespoon at a time, kneading between additions.",
      });
    } else if (butterStep !== -1 && glutenStep === -1) {
      warnings.push({
        kind: "gluten_development_unclear",
        severity: "warning",
        message: "Recipe doesn't mention kneading to windowpane or gluten development before adding butter.",
        suggestion: "Brioche needs the gluten network built first. Add a knead step (8-12 min stand mixer, or until windowpane) before butter.",
      });
    }

    // Brioche knead-timer too short — if the knead step has a timer
    // under 5 min, the gluten won't develop even if the order is right.
    if (glutenStep !== -1) {
      const kneadStep = steps[glutenStep];
      const kneadSec = Number(kneadStep?.timer) || Number(kneadStep?.timerSec) || 0;
      if (kneadSec > 0 && kneadSec < 300) {
        issues.push({
          kind: "knead_too_short",
          severity: "blocker",
          message: `Knead timer is ${Math.round(kneadSec / 60)} min — not enough time for gluten development in enriched dough.`,
          suggestion: "Aim 8-12 min stand mixer on medium, or longer by hand, until the dough passes the windowpane test.",
        });
      }
    }
  }

  // CHECK 5 — yeast without proof time. Yeast needs warmth + time;
  // a recipe that says "add yeast, bake immediately" won't rise.
  if (grams.yeast > 0) {
    const hasProofStep = steps.some((s) =>
      /\b(rise|proof|prove|ferment|double in size|until doubled)\b/i.test(stepText(s))
    );
    const hasLongTimer = steps.some((s) => {
      const sec = Number(s.timer) || Number(s.timerSec) || 0;
      return sec >= 30 * 60;
    });
    if (!hasProofStep && !hasLongTimer) {
      issues.push({
        kind: "yeast_no_proof",
        severity: "blocker",
        message: "Recipe contains yeast but no proof / rise step (no instruction to let dough rise, no timer ≥ 30 min).",
        suggestion: "Add a rise step after mixing: cover, warm spot, until roughly doubled (typically 60-90 min for first rise).",
      });
    }

    // Yeast touching salt directly — common bug. Detect step text that
    // mentions both, with no intervening liquid. Salt on dry yeast
    // dehydrates it on contact.
    const yeastSaltStep = steps.findIndex((s) => {
      const t = stepText(s).toLowerCase();
      return /yeast/.test(t) && /\bsalt\b/.test(t) && !/(water|milk|liquid|warm)/.test(t);
    });
    if (yeastSaltStep !== -1) {
      issues.push({
        kind: "yeast_salt_contact",
        severity: "blocker",
        message: "Recipe combines yeast and salt directly without intervening liquid. Salt on dry yeast kills it on contact.",
        suggestion: "Dissolve yeast in warm liquid first, then add salt to the flour separately. Mix the dry side and wet side, then combine.",
      });
    }

    // Cold liquid + yeast — needs warmth (~100-110°F) to activate.
    const yeastStep = steps.findIndex((s) =>
      /\byeast\b/i.test(stepText(s)) && /\b(water|milk)\b/i.test(stepText(s))
    );
    if (yeastStep !== -1) {
      const t = stepText(steps[yeastStep]);
      if (!/\b(warm|lukewarm|tepid|110\s*°?f?|100[\s-]110)\b/i.test(t)) {
        warnings.push({
          kind: "yeast_cold_liquid",
          severity: "warning",
          message: "Yeast step doesn't specify warm liquid (100-110°F / 38-43°C). Cold liquid won't activate active dry yeast.",
          suggestion: "Specify 'warm' or 'lukewarm' liquid — too hot (>115°F) kills yeast, too cold (<85°F) won't activate it.",
        });
      }
    }
  }

  // CHECK 6 — flour type mismatch. Soft constraint; some legitimate
  // substitutions exist (AP-flour cookies are fine, AP-flour brioche
  // is noticeably weaker). Warning only.
  if (PREFERRED_FLOUR[type]) {
    const flourUsed = ings
      .filter((i) => FLOUR_IDS.has(i.ingredientId))
      .map((i) => i.ingredientId);
    const matches = flourUsed.some((f) => PREFERRED_FLOUR[type].includes(f));
    if (flourUsed.length > 0 && !matches) {
      warnings.push({
        kind: "flour_type",
        severity: "warning",
        message: `${prettyType(type)} typically uses ${PREFERRED_FLOUR[type].join(" or ")}. Recipe uses ${flourUsed.join(", ")}.`,
        suggestion: `For best results, switch to ${PREFERRED_FLOUR[type][0]}. The recipe will work with the current flour but expect a weaker structure.`,
      });
    }
  }

  // CHECK 7 — cookie spread target undeclared. Warning only — recipe
  // can still work, but ambiguity here is why "the cookies didn't
  // spread enough" complaints happen.
  if (type === "cookie") {
    const cuesText = steps.map(stepText).join(" ").toLowerCase();
    const hasSpreadCue = /\b(chewy|crispy|crunchy|cakey|soft|crisp edges)\b/.test(cuesText)
      || /\b(chewy|crispy|crunchy|cakey)\b/.test((recipe.description || "").toLowerCase());
    if (!hasSpreadCue) {
      warnings.push({
        kind: "cookie_spread_unclear",
        severity: "warning",
        message: "Recipe doesn't specify whether the cookie should be chewy, crispy, or cakey.",
        suggestion: "Spread depends on butter temperature × sugar mix × leavening choice. Pick a target and check that ingredients match: chewy needs melted butter + brown sugar dominant + baking soda; crispy needs softened butter + white sugar + baking powder.",
      });
    }
  }

  // CHECK 8 — fat / sugar / egg / salt / yeast / oil bands. The
  // remaining BANDS entries that have specific quantity guidance.
  // Lower priority than leavening + hydration but still useful —
  // keeps the LLM honest about cake-vs-muffin distinctions and
  // catches "tastes flat" / "won't rise" cases.
  if (BANDS[type]?.fat) {
    rangeCheck(ratios.fat, BANDS[type].fat, {
      kind: "fat_ratio",
      label: "Fat",
      unit: "% of flour weight",
      remedy: () => "Adjust butter/oil quantity to bring into typical band for this style.",
    }, issues, warnings);
  }
  if (BANDS[type]?.sugar) {
    rangeCheck(ratios.sugar, BANDS[type].sugar, {
      kind: "sugar_ratio",
      label: "Sugar",
      unit: "% of flour weight",
      remedy: () => "Adjust sugar quantity to bring into typical band for this style.",
    }, issues, warnings);
  }
  if (BANDS[type]?.eggs) {
    rangeCheck(ratios.eggs, BANDS[type].eggs, {
      kind: "eggs_ratio",
      label: "Eggs",
      unit: "% of flour weight",
      remedy: () => "Adjust egg quantity. Standard large egg is ~50g; the % above is total egg weight relative to flour.",
    }, issues, warnings);
  }
  if (BANDS[type]?.salt) {
    rangeCheck(ratios.salt, BANDS[type].salt, {
      kind: "salt_ratio",
      label: "Salt",
      unit: "% of flour weight",
      remedy: () => "Target 1.5-2% of flour weight. Under 1% tastes flat; over 3% slows yeast and tastes salty.",
    }, issues, warnings);
  }
  if (BANDS[type]?.yeast) {
    rangeCheck(ratios.yeast, BANDS[type].yeast, {
      kind: "yeast_ratio",
      label: "Yeast",
      unit: "% of flour weight",
      remedy: () => "Typical 0.5-1% (instant) or 1-2% (active dry). More yeast = faster but boozy flavor; less = slower but better flavor development.",
    }, issues, warnings);
  }
  if (BANDS[type]?.oil) {
    rangeCheck(ratios.oil, BANDS[type].oil, {
      kind: "oil_ratio",
      label: "Olive oil",
      unit: "% of flour weight",
      remedy: () => "Focaccia wants 4-12% olive oil — under that, it's just flatbread.",
    }, issues, warnings);
  }

  return {
    ok: issues.length === 0,
    type,
    hydrationPct: ratios.hydration,
    ratios,
    issues,
    warnings,
  };
}

// ─── Internals ──────────────────────────────────────────────────────

function sumGramsByRole(ings, ctx = {}) {
  const totals = {
    flour: 0, fat: 0, sugar: 0, liquid: 0, eggs: 0, salt: 0,
    yeast: 0, bakingSoda: 0, bakingPowder: 0, chemicalLeavening: 0,
    oil: 0,
  };
  for (const ing of ings) {
    const id = ing.ingredientId;
    if (!id) continue;
    const g = gramsOf(ing, ctx);
    if (g === 0) continue;
    if (FLOUR_IDS.has(id)) totals.flour += g;
    else if (FAT_IDS.has(id)) {
      totals.fat += g;
      if (id === "olive_oil") totals.oil += g;
    }
    else if (SUGAR_IDS.has(id)) totals.sugar += g;
    else if (EGG_IDS.has(id)) totals.eggs += g;
    else if (SALT_IDS.has(id)) totals.salt += g;
    else if (LEAVENING.yeast.has(id)) totals.yeast += g;
    else if (LEAVENING.baking_soda.has(id)) {
      totals.bakingSoda += g;
      totals.chemicalLeavening += g;
    }
    else if (LEAVENING.baking_powder.has(id)) {
      totals.bakingPowder += g;
      totals.chemicalLeavening += g;
    }
    if (LIQUID_IDS.has(id)) totals.liquid += g;
  }
  return totals;
}

// Egg/yeast count-to-grams. convertWithBridge can fail for count-based
// ingredients when the canonical's ladder doesn't carry a count entry.
// Hardcoded fallbacks for the most common shapes.
const COUNT_GRAMS = {
  egg:        50,  // standard large egg
  eggs:       50,
  large_egg:  50,
  egg_yolk:   18,
  egg_white:  33,
};
const PACKET_GRAMS = {
  active_dry_yeast: 7,
  instant_yeast:    7,
  fresh_yeast:      17,  // standard cake of fresh yeast
};

function gramsOf(ing, ctx) {
  const id = ing.ingredientId;
  if (!id) return 0;
  const ingDef = ctx.findIngredient ? ctx.findIngredient(id) : null;
  if (!ingDef) return 0;

  // Prefer structured qty when present (matches nutrition.js pattern).
  const qty = ing.qty || parseAmountString(ing.amount, ingDef);
  if (!qty || !Number.isFinite(qty.amount)) return 0;

  const unit = (qty.unit || "").toLowerCase();

  // Count-based fallback for eggs (and similar). convertWithBridge's
  // count bridge needs a canonical-stamped grams-per-count; bundled
  // canonicals don't always have it for eggs.
  if (unit === "count" || unit === "large" || unit === "small" || unit === "medium") {
    if (COUNT_GRAMS[id] != null) return qty.amount * COUNT_GRAMS[id];
  }

  // Packet fallback for yeast — convertWithBridge has no concept of
  // a "packet."
  if (unit === "packet" || unit === "package" || unit === "pkg") {
    if (PACKET_GRAMS[id] != null) return qty.amount * PACKET_GRAMS[id];
  }

  try {
    const result = convertWithBridge(qty, "g", ingDef);
    return result?.ok && Number.isFinite(result.value) ? result.value : 0;
  } catch {
    return 0;
  }
}

function hasAcid(ings) {
  return ings.some((i) => ACID_IDS.has(i.ingredientId));
}

function findStepIndex(steps, pattern) {
  return steps.findIndex((s) => pattern.test(stepText(s)));
}

function rangeCheck(value, { warn, block }, spec, issues, warnings) {
  if (!Number.isFinite(value)) return;
  if (value < block[0] || value > block[1]) {
    issues.push({
      kind: spec.kind,
      severity: "blocker",
      message: `${spec.label} is ${value.toFixed(1)}${spec.unit.startsWith("%") ? "%" : " " + spec.unit} — outside the workable range (${block[0]}-${block[1]}).`,
      suggestion: spec.remedy(value),
    });
  } else if (value < warn[0] || value > warn[1]) {
    warnings.push({
      kind: spec.kind,
      severity: "warning",
      message: `${spec.label} is ${value.toFixed(1)}${spec.unit.startsWith("%") ? "%" : " " + spec.unit} — outside the typical range (${warn[0]}-${warn[1]}) but should still work.`,
      suggestion: spec.remedy(value),
    });
  }
}

function leaveningRemedy(type) {
  const targets = {
    cookie:      "0.5-2% (about ½ tsp baking soda OR 1 tsp baking powder per cup of flour)",
    cake:        "2-5% (about 1-1.5 tsp baking powder per cup of flour)",
    quick_bread: "3-6% (about 1-1.5 tsp baking powder per cup of flour, more for muffins)",
    pancake:     "3-8% (about 1-2 tsp baking powder per cup of flour)",
  };
  return () => `Adjust leavening to ${targets[type] || "standard amounts for this style"}.`;
}

function hydrationRemedy(type) {
  const targets = {
    lean_bread:     "60-75% — typical sourdough/baguette range",
    enriched_bread: "48-62% — eggs and butter contribute fat/liquid, so total hydration is lower",
    focaccia:       "72-88% — wet, slack dough that needs olive oil hands",
    pancake:        "80-130% — batter, not dough",
  };
  return () => `Target hydration: ${targets[type] || "match the typical range for this style"}.`;
}

function prettyType(type) {
  return ({
    enriched_bread: "Enriched bread (brioche, challah)",
    lean_bread:     "Lean bread (baguette, country loaf)",
    focaccia:       "Focaccia",
    cookie:         "Cookies",
    cake:           "Cake",
    quick_bread:    "Quick bread / muffins",
    pancake:        "Pancakes",
  })[type] || type;
}
